<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3 hide-on-print">
      <SimplePageHeader title="Commissions" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="cloud_download"
            color="primary"
            text="Excel"
            :block="true"
            @click="handleExportToExcel"
          />
          <ButtonPrint
            class="mt-3"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <CommissionReportFilters
        class="px-5 mb-sm-5"
        @search="handleSearch"
        @clear="handleSearch"
      ></CommissionReportFilters>
    </div>
    <ReportHeader
      reportTitle="Commission Report"
      :reportCriteria="reportFilterCriteria"
    ></ReportHeader>
    <v-row>
      <v-col cols="12">
        <CommissionReportItemsTable
          class="px-0 pt-2"
          :commissionReportItems="commissionReportItems"
          :options.sync="options"
          :serverItemsLength="serverItemsLength"
        />
      </v-col>
    </v-row>
    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import CommissionReportFilters from './CommissionReportFilters'
import { ButtonPrint, BottomSpacer, ButtonCustomIcon } from '@/components/buttons'
import ReportHeader from '@/components/reports/ReportHeader'
import CommissionReportItemsTable from './CommissionReportItemsTable'
import { SimplePageHeader } from '@/components/pageComponents'
import excel from '@/lib/export/excel'
import { progresModlaCommands } from '@/lib/progressModal'
import { formatCurrency } from '@/lib/display'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  mixins: [
    progresModlaCommands
  ],
  components: {
    CommissionReportFilters,
    ButtonPrint,
    BottomSpacer,
    ReportHeader,
    CommissionReportItemsTable,
    SimplePageHeader,
    ButtonCustomIcon
  },
  data () {
    return {
      hasFetchedAllCommissionReportItems: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['employeeName'],
        sortDesc: [false]
      }
    }
  },
  computed: {
    ...mapFields('commissionReport', [
      'commissionReportItems',
      'totalRecords'
    ]),
    ...mapFields('commissionReportFilters', [
      'businessEntityId',
      'userId',
      'month',
      'year'
    ]),
    reportFilterCriteria: {
      get () {
        let months = [ 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December' ]
        return [
          { 'key': 'Month', 'value': months[parseInt(this.month) - 1] + ' - ' + parseInt(this.year) }
        ]
      }
    },
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()

        if (!this.shouldFetchCommissionReportItems(newOptions, previousOptions)) {
          return
        }

        this.fetchCommissionReportItems()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('commissionReport', [
      'getCommissionReportWithFilters',
      'getCommissionReportWithFiltersPaginated'
    ]),
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { businessEntityId, userId, year, month } = this.$route.query

      const parsedBusinessEntityId = Number(businessEntityId)
      this.businessEntityId = Number.isInteger(parsedBusinessEntityId) && parsedBusinessEntityId > 0
        ? parsedBusinessEntityId
        : null

      const parsedUserId = Number(userId)
      this.userId = Number.isInteger(parsedUserId) && parsedUserId > 0 ? parsedUserId : null

      const parsedYear = Number(year)
      this.year = Number.isInteger(parsedYear) && parsedYear > 0 ? parsedYear : new Date().getFullYear()

      const parsedMonth = Number(month)
      this.month = Number.isInteger(parsedMonth) && parsedMonth >= 1 && parsedMonth <= 12
        ? parsedMonth
        : (new Date().getMonth() + 1)
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)
      const nextQuery = { ...this.$route.query, ...paginationParams }

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.businessEntityId)
      setOrDeleteQueryParam(nextQuery, 'userId', this.userId)
      setOrDeleteQueryParam(nextQuery, 'year', this.year)
      setOrDeleteQueryParam(nextQuery, 'month', this.month)

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'businessEntityId', 'userId', 'year', 'month'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchCommissionReportItems () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      if (itemsPerPage === -1) {
        this.getCommissionReportWithFilters().then(() => {
          this.hasFetchedAllCommissionReportItems = true
        })
        return
      }

      this.hasFetchedAllCommissionReportItems = false
      this.getCommissionReportWithFiltersPaginated({
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      })
    },
    shouldFetchCommissionReportItems (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllCommissionReportItems)
    },
    handleSearch () {
      this.options = { ...this.options, page: 1 }
    },
    handleExportToExcel () {
      if (this.commissionReportItems && this.commissionReportItems.length > 0) {
        let data = []
        for (let item of this.commissionReportItems) {
          data.push({
            month: item.month,
            year: item.year,
            employeeName: item.employeeName,
            totalCommission: formatCurrency(item.totalCommission),
            totalIndividualGP: formatCurrency(item.totalIndividualGP),
            consultantsGP: formatCurrency(item.consultantsGP),
            permPlacementsGP: formatCurrency(item.permPlacementsGP),
            commissionOverride: formatCurrency(item.commissionOverride)
          })
        }
        let headers = ['Month', 'Year', 'Employee', 'Commission', 'Individual GP', 'Consultants GP', 'Perm. Placements GP', 'Override']
        let columns = ['month', 'year', 'employeeName', 'totalCommission', 'totalIndividualGP', 'consultantsGP', 'permPlacementsGP', 'commissionOverride']
        const params = {
          title: headers,
          key: columns,
          data: data,
          autoWidth: true,
          filename: `Commissions`
        }
        excel.exportArray2Excel(params)
      }
    }
  },
  created () {
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  }
}
</script>
